import type { Appearance, CustomFontSource } from '@stripe/stripe-js';
import Theme, { DarkBrand, LightBrand } from '../../Theme';
import dmSansLatin400 from '@fontsource/dm-sans/files/dm-sans-latin-400-normal.woff2?url';
import dmSansLatin500 from '@fontsource/dm-sans/files/dm-sans-latin-500-normal.woff2?url';
import dmSansLatin700 from '@fontsource/dm-sans/files/dm-sans-latin-700-normal.woff2?url';
import dmSansLatinExt400 from '@fontsource/dm-sans/files/dm-sans-latin-ext-400-normal.woff2?url';
import dmSansLatinExt500 from '@fontsource/dm-sans/files/dm-sans-latin-ext-500-normal.woff2?url';
import dmSansLatinExt700 from '@fontsource/dm-sans/files/dm-sans-latin-ext-700-normal.woff2?url';

/**
 * The one set of tokens the Stripe Elements checkout is drawn with: Stripe's `appearance` below and
 * this shop's own rows (PaymentMethodOption's Variant.StripeElements, the saved card, cash, PayPal)
 * both read them, so the rows Stripe draws and the rows the shop draws cannot drift apart. Colours
 * come from the Theme the Tailwind classes are generated from, and stay hex: Stripe's appearance
 * takes no `var()` and no `rgb(var(--…))`.
 */
export const STRIPE_ROW_METRICS = {
  radiusPx: 12,
  paddingPx: 12,
  gapPx: 12,
  lineHeightPx: 20,
  labelSizePx: 14,
  labelWeight: 500,
  secondarySizePx: 13,
  // The card fields and their labels, at the size of the checkout's own inputs (Tailwind's `sm`).
  inputSizePx: 14,
  radioPx: 16,
  iconPx: 16,
} as const;

/**
 * Stripe's radio as its Payment Element draws it (read from the iframe's own bundle): an SVG on a
 * 100-unit viewBox, a ring of radius 44 whose stroke is clipped to the circle — so only its inner
 * half shows — and a dot of radius 21.875 when checked. The appearance below sets only the colours
 * and the size, and the shop's own radio (PaymentMethodOption) is drawn from these same numbers, so
 * the two match at any size without depending on what Stripe makes of an override.
 */
export const STRIPE_RADIO_GEOMETRY = {
  viewBox: 100,
  ringRadius: 44,
  ringStrokeWidth: 9.375,
  dotRadius: 21.875,
} as const;

export interface StripeRowTokens {
  border: string;
  background: string;
  selectedBorder: string;
  selectedBackground: string;
  radio: string;
  radioChecked: string;
  textSecondary: string;
}

export const STRIPE_ROW_TOKENS: Record<'light' | 'dark', StripeRowTokens> = {
  light: {
    border: Theme.color.gray.DEFAULT,
    background: Theme.color.white.DEFAULT,
    selectedBorder: Theme.color.blue['300'],
    selectedBackground: Theme.color.blue['200'],
    radio: Theme.color.gray['1000'],
    radioChecked: Theme.color.blue['300'],
    textSecondary: LightBrand.text.secondary,
  },
  dark: {
    border: DarkBrand.border,
    background: Theme.color.black.DEFAULT,
    selectedBorder: Theme.color.blue['300'],
    selectedBackground: Theme.color.black['100'],
    radio: DarkBrand.border,
    radioChecked: Theme.color.blue['300'],
    textSecondary: DarkBrand.text.secondary,
  },
};

export const stripeRowTokens = (isDarkMode: boolean): StripeRowTokens =>
  isDarkMode ? STRIPE_ROW_TOKENS.dark : STRIPE_ROW_TOKENS.light;

const px = (value: number) => `${value}px`;

/** Latin plus Latin Extended, for the Polish and Romanian catalogs. */
const LATIN_RANGE =
  'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD';
const LATIN_EXT_RANGE =
  'U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF';

/**
 * DM Sans for Stripe's iframe, from this shop's own build: Google Fonts stays off (the GDPR decision
 * in index.html), so the self-hosted @fontsource files go over as absolute URLs, which the iframe
 * fetches cross-origin (vercel.json allows it for the woff2 assets). Stripe takes HTTPS sources only;
 * on a plain-HTTP page (local development) the Element keeps the system stack.
 *
 * A module constant: the Elements read `fonts` at creation, and a new array per render would make
 * react-stripe-js re-apply it.
 */
export const STRIPE_ELEMENTS_FONTS: CustomFontSource[] =
  typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? (
        [
          [dmSansLatin400, '400', LATIN_RANGE],
          [dmSansLatin500, '500', LATIN_RANGE],
          [dmSansLatin700, '700', LATIN_RANGE],
          [dmSansLatinExt400, '400', LATIN_EXT_RANGE],
          [dmSansLatinExt500, '500', LATIN_EXT_RANGE],
          [dmSansLatinExt700, '700', LATIN_EXT_RANGE],
        ] as const
      ).map(([file, weight, unicodeRange]) => ({
        family: 'DM Sans',
        src: `url(${new URL(file, window.location.origin).href})`,
        weight,
        style: 'normal',
        display: 'swap',
        unicodeRange,
      }))
    : [];

/**
 * Stripe.js's own test for a browser whose iframe URL it keeps short: the legacy EdgeHTML Edge
 * (`Edge/`, not Chromium's `Edg/`) or Internet Explorer. In such a browser, when an Element's
 * creation options run past 2,000 characters, Stripe.js leaves the fonts out of the creation and
 * passes them in a `update({ fonts })` straight after — which the Payment Element refuses with
 * "fonts should be used in elements() instead", throwing out of `elements.create()` and taking the
 * whole checkout into the ErrorBoundary (SHOP-WEB-5R). Our DM Sans sources alone are ~2.9k.
 */
export const stripeDefersFontsToUpdate = (userAgent: string): boolean =>
  /edge\//i.test(userAgent) || /MSIE \d|Trident\//i.test(userAgent);

/**
 * The `fonts` part of the Elements options: left out entirely where Stripe.js would hand them to the
 * Payment Element's update (see stripeDefersFontsToUpdate). Its check lets `fonts: undefined` pass but
 * not an empty array, so the key is omitted, not emptied; the Elements keep the system stack there.
 */
export const stripeElementsFontsOption = (
  userAgent: string,
  fonts: CustomFontSource[] = STRIPE_ELEMENTS_FONTS,
): { fonts?: CustomFontSource[] } => (stripeDefersFontsToUpdate(userAgent) ? {} : { fonts });

/** Read once, as Stripe.js reads the user agent once when it loads. */
export const STRIPE_ELEMENTS_FONTS_OPTION = stripeElementsFontsOption(
  typeof navigator !== 'undefined' ? navigator.userAgent : '',
);

/**
 * The Payment Element and the Express Checkout Element drawn as this checkout's own rows.
 */
export const buildStripeElementsAppearance = (theme: {
  isDarkMode: boolean;
  color: { brand: { text: { primary: string } } };
}): Appearance => {
  const isDark = theme.isDarkMode;
  const tokens = stripeRowTokens(isDark);
  const textPrimary = theme.color.brand.text.primary;

  return {
    theme: isDark ? 'night' : 'stripe',
    variables: {
      colorPrimary: tokens.radioChecked,
      colorText: textPrimary,
      colorTextSecondary: tokens.textSecondary,
      colorBackground: tokens.background,
      iconColor: tokens.textSecondary,
      // Monochrome method logos, as the shop's own rows draw theirs.
      logoColor: isDark ? 'light' : 'dark',
      borderRadius: px(STRIPE_ROW_METRICS.radiusPx),
      fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
      // Stripe's iframe smooths antialiased by default, which draws the same 500 lighter on macOS
      // than the page's own rows, which keep the browser's smoothing.
      fontSmooth: 'auto',
      accordionItemSpacing: px(STRIPE_ROW_METRICS.gapPx),
      accordionItemLabelFontSize: px(STRIPE_ROW_METRICS.labelSizePx),
      accordionItemLabelFontWeight: String(STRIPE_ROW_METRICS.labelWeight),
      accordionItemLabelSelectedFontWeight: String(STRIPE_ROW_METRICS.labelWeight),
      accordionItemLabelColorText: textPrimary,
      accordionItemLabelSelectedColorText: textPrimary,
    },
    rules: {
      '.AccordionItem': {
        border: `1px solid ${tokens.border}`,
        borderRadius: px(STRIPE_ROW_METRICS.radiusPx),
        boxShadow: 'none',
        padding: px(STRIPE_ROW_METRICS.paddingPx),
        lineHeight: px(STRIPE_ROW_METRICS.lineHeightPx),
        backgroundColor: tokens.background,
      },
      '.AccordionItem--selected': {
        backgroundColor: tokens.selectedBackground,
        borderColor: tokens.selectedBorder,
        color: textPrimary,
      },
      // The page's maximum-scale=1 already keeps iOS from zooming on focus, the iframe included.
      '.Input': {
        fontSize: px(STRIPE_ROW_METRICS.inputSizePx),
      },
      '.Label': {
        fontSize: px(STRIPE_ROW_METRICS.inputSizePx),
      },
      // Colours and size only: the ring and the dot keep Stripe's own geometry (STRIPE_RADIO_GEOMETRY).
      '.RadioIcon': {
        width: px(STRIPE_ROW_METRICS.radioPx),
      },
      '.RadioIconOuter': {
        stroke: tokens.radio,
        fill: tokens.background,
      },
      '.RadioIconOuter--checked': {
        stroke: tokens.radioChecked,
      },
      '.RadioIconInner--checked': {
        fill: tokens.radioChecked,
      },
    },
  };
};
